import React, { useState } from 'react';
import {
  Title,
  Group,
  Button,
  Text,
  Stack,
  Grid,
  Card,
  Badge,
  Select,
  Tabs,
  ThemeIcon,
  Progress,
  SimpleGrid,
  Table,
  ActionIcon,
  Menu,
  rem,
  Divider,
  Alert,
  Modal,
} from '@mantine/core';
import {
  IconActivity,
  IconTrendingUp,
  IconTrendingDown,
  IconUsers,
  IconCamera,
  IconAlertTriangle,
  IconDownload,
  IconChartBar,
  IconRefresh,
  IconFileExport,
  IconMail,
  IconEye,
  IconDots,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { AreaChart, BarChart, LineChart, DonutChart } from '@mantine/charts';
import { useReports, useGenerateReport, useExportReport } from '../../hooks/useReports';
import { useDetectionStats } from '../../hooks/useDetections';
import { useStudents } from '../../hooks/useStudents';
import { useCameras } from '../../hooks/useCameras';

const Reports: React.FC = () => {
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    new Date(new Date().setDate(new Date().getDate() - 30)),
    new Date()
  ]);
  const [reportType, setReportType] = useState<string>('activities');
  const [schoolFilter] = useState<string>('');
  const [areaFilter, setAreaFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  
  const [exportModalOpened, { open: openExportModal, close: closeExportModal }] = useDisclosure(false);

  const {
    isLoading: reportsLoading,
    // error: reportsError,
    refetch: refetchReports
  } = useReports({
    start_date: dateRange[0]?.toISOString().split('T')[0],
    end_date: dateRange[1]?.toISOString().split('T')[0],
    report_type: reportType,
    filters: {
      ...(schoolFilter && { school_id: schoolFilter }),
      ...(areaFilter && { area_id: areaFilter }),
    },
  });

  const {
    data: detectionStats,
    // isLoading: statsLoading,
  } = useDetectionStats({
    start_date: dateRange[0]?.toISOString().split('T')[0],
    end_date: dateRange[1]?.toISOString().split('T')[0],
  });

  const { data: studentsData } = useStudents({ view: 'summary' });
  useCameras({ view: 'summary' });

  const generateReportMutation = useGenerateReport();
  const exportReportMutation = useExportReport();

  const students = studentsData?.students || [];
  // const cameras = camerasData?.cameras || [];
  // const reports = reportsData?.reports || [];

  // Mock data for charts - in real app, this would come from API
  const activityTrendData = [
    { date: '2024-01', activities: 120, incidents: 2 },
    { date: '2024-02', activities: 145, incidents: 1 },
    { date: '2024-03', activities: 180, incidents: 3 },
    { date: '2024-04', activities: 165, incidents: 0 },
    { date: '2024-05', activities: 200, incidents: 1 },
    { date: '2024-06', activities: 195, incidents: 2 },
  ];

  const activityTypeData = [
    { name: 'Entry/Exit', value: 45, color: 'blue' },
    { name: 'Play', value: 30, color: 'green' },
    { name: 'Meal', value: 15, color: 'orange' },
    { name: 'Sleep', value: 8, color: 'violet' },
    { name: 'Safety Concerns', value: 2, color: 'red' },
  ];

  const hourlyActivityData = [
    { hour: '7:00', activities: 15 },
    { hour: '8:00', activities: 45 },
    { hour: '9:00', activities: 35 },
    { hour: '10:00', activities: 55 },
    { hour: '11:00', activities: 40 },
    { hour: '12:00', activities: 65 },
    { hour: '13:00', activities: 50 },
    { hour: '14:00', activities: 35 },
    { hour: '15:00', activities: 45 },
    { hour: '16:00', activities: 30 },
    { hour: '17:00', activities: 20 },
    { hour: '18:00', activities: 10 },
  ];

  const cameraPerformanceData = [
    { camera: 'Playground Cam 1', uptime: 98.5, detections: 245 },
    { camera: 'Entrance Cam', uptime: 99.2, detections: 189 },
    { camera: 'Classroom Cam A', uptime: 97.8, detections: 156 },
    { camera: 'Dining Hall Cam', uptime: 96.5, detections: 203 },
    { camera: 'Sleep Area Cam', uptime: 99.8, detections: 87 },
  ];

  const handleGenerateReport = async () => {
    try {
      const response = await generateReportMutation.mutateAsync({
        type: reportType,
        filters: {
          start_date: dateRange[0]?.toISOString(),
          end_date: dateRange[1]?.toISOString(),
          filters: {
            school_id: schoolFilter || undefined,
            area_id: areaFilter || undefined,
          }
        },
      });
      
      // Automatically set the generated report ID for export
      if (response && response.id) {
        setSelectedReportId(response.id);
      }
      
      notifications.show({
        title: 'Report Generated',
        message: 'Report has been generated successfully. You can now export it.',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to generate report',
        color: 'red',
      });
    }
  };

  const handleExportReport = async (format: string) => {
    if (!selectedReportId) {
      notifications.show({
        title: 'No Report Selected',
        message: 'Please select a report to export',
        color: 'yellow',
      });
      return;
    }

    try {
      await exportReportMutation.mutateAsync({
        report_id: selectedReportId,
        format: format as 'pdf' | 'excel' | 'csv',
      });
      notifications.show({
        title: 'Export Started',
        message: `Report export in ${format.toUpperCase()} format has started`,
        color: 'blue',
      });
      closeExportModal();
    } catch (error) {
      notifications.show({
        title: 'Export Failed',
        message: 'Failed to export report',
        color: 'red',
      });
    }
  };


  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={2}>Activity Reports & Analytics</Title>
          <Text size="sm" c="dimmed">
            Comprehensive insights into student activities and system performance
          </Text>
        </div>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetchReports()}
            loading={reportsLoading}
          >
            Refresh
          </Button>
          <Button
            leftSection={<IconFileExport size={16} />}
            onClick={openExportModal}
          >
            Export Report
          </Button>
        </Group>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <DatePickerInput
            type="range"
            label="Date Range"
            placeholder="Select date range"
            value={dateRange}
            onChange={setDateRange}
            style={{ minWidth: 200 }}
          />
          <Select
            label="Report Type"
            placeholder="Select report type"
            data={[
              { value: 'activities', label: 'Activity Report' },
              { value: 'safety', label: 'Safety Report' },
              { value: 'attendance', label: 'Attendance Report' },
              { value: 'performance', label: 'System Performance' },
              { value: 'summary', label: 'Daily Summary' },
            ]}
            value={reportType}
            onChange={(value) => setReportType(value || 'activities')}
            style={{ minWidth: 180 }}
          />
          <Select
            label="Filter by Area"
            placeholder="All areas"
            data={[
              { value: '', label: 'All Areas' },
              { value: '1', label: 'Playground' },
              { value: '2', label: 'Classroom A' },
              { value: '3', label: 'Dining Hall' },
              { value: '4', label: 'Sleep Area' },
            ]}
            value={areaFilter}
            onChange={(value) => setAreaFilter(value || '')}
            style={{ minWidth: 150 }}
          />
          <Button
            leftSection={<IconChartBar size={16} />}
            onClick={handleGenerateReport}
            loading={generateReportMutation.isPending}
          >
            Generate Report
          </Button>
        </Group>
      </Card>

      {/* Summary Cards */}
      <Grid>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="blue" variant="light" size="lg">
                <IconActivity size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Total Activities</Text>
                <Text fw={700} size="xl">
                  {(detectionStats as any)?.total_detections || 1247}
                </Text>
                <Group gap={4} mt={2}>
                  <IconTrendingUp size={14} color="green" />
                  <Text size="xs" c="green" fw={500}>+12%</Text>
                </Group>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="green" variant="light" size="lg">
                <IconUsers size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Active Students</Text>
                <Text fw={700} size="xl">{students.length}</Text>
                <Group gap={4} mt={2}>
                  <IconTrendingUp size={14} color="green" />
                  <Text size="xs" c="green" fw={500}>+3%</Text>
                </Group>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="orange" variant="light" size="lg">
                <IconCamera size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Camera Uptime</Text>
                <Text fw={700} size="xl">98.3%</Text>
                <Group gap={4} mt={2}>
                  <IconTrendingDown size={14} color="red" />
                  <Text size="xs" c="red" fw={500}>-0.5%</Text>
                </Group>
              </div>
            </Group>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <Card withBorder>
            <Group>
              <ThemeIcon color="red" variant="light" size="lg">
                <IconAlertTriangle size={20} />
              </ThemeIcon>
              <div>
                <Text size="xs" c="dimmed">Safety Incidents</Text>
                <Text fw={700} size="xl">3</Text>
                <Group gap={4} mt={2}>
                  <IconTrendingDown size={14} color="green" />
                  <Text size="xs" c="green" fw={500}>-50%</Text>
                </Group>
              </div>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Main Reports */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="activities" leftSection={<IconActivity size={16} />}>
            Activities
          </Tabs.Tab>
          <Tabs.Tab value="performance" leftSection={<IconCamera size={16} />}>
            Performance
          </Tabs.Tab>
          <Tabs.Tab value="trends" leftSection={<IconTrendingUp size={16} />}>
            Trends
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card>
                <Group justify="space-between" mb="md">
                  <Title order={4}>Activity Trends (Last 30 Days)</Title>
                  <Group>
                    <Badge color="blue" variant="light">Activities</Badge>
                    <Badge color="red" variant="light">Incidents</Badge>
                  </Group>
                </Group>
                <AreaChart
                  h={300}
                  data={activityTrendData}
                  dataKey="date"
                  series={[
                    { name: 'activities', color: 'blue.6' },
                    { name: 'incidents', color: 'red.6' },
                  ]}
                  curveType="linear"
                />
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Card>
                <Title order={4} mb="md">Activity Distribution</Title>
                <DonutChart
                  data={activityTypeData}
                  size={200}
                  thickness={30}
                />
                <Stack gap="xs" mt="md">
                  {activityTypeData.map((item) => (
                    <Group key={item.name} justify="space-between">
                      <Group gap="xs">
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: `var(--mantine-color-${item.color}-6)`,
                          }}
                        />
                        <Text size="xs">{item.name}</Text>
                      </Group>
                      <Text size="xs" fw={500}>{item.value}%</Text>
                    </Group>
                  ))}
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="activities" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 8 }}>
              <Card>
                <Title order={4} mb="md">Hourly Activity Pattern</Title>
                <BarChart
                  h={300}
                  data={hourlyActivityData}
                  dataKey="hour"
                  series={[{ name: 'activities', color: 'blue.6' }]}
                />
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Stack gap="md">
                <Card>
                  <Title order={5} mb="md">Peak Activity Times</Title>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text size="sm">Morning Rush</Text>
                      <Badge color="blue">8:00-9:00 AM</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Lunch Time</Text>
                      <Badge color="orange">12:00-1:00 PM</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text size="sm">Play Time</Text>
                      <Badge color="green">10:00-11:00 AM</Badge>
                    </Group>
                  </Stack>
                </Card>
                
                <Card>
                  <Title order={5} mb="md">Activity Quality Score</Title>
                  <Stack gap="md">
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Detection Accuracy</Text>
                        <Text size="sm" fw={500}>94.2%</Text>
                      </Group>
                      <Progress value={94.2} color="green" />
                    </div>
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Verification Rate</Text>
                        <Text size="sm" fw={500}>87.5%</Text>
                      </Group>
                      <Progress value={87.5} color="blue" />
                    </div>
                    <div>
                      <Group justify="space-between" mb="xs">
                        <Text size="sm">Response Time</Text>
                        <Text size="sm" fw={500}>92.1%</Text>
                      </Group>
                      <Progress value={92.1} color="orange" />
                    </div>
                  </Stack>
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="performance" pt="md">
          <Card>
            <Title order={4} mb="md">Camera Performance Summary</Title>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Camera</Table.Th>
                  <Table.Th>Uptime</Table.Th>
                  <Table.Th>Detections</Table.Th>
                  <Table.Th>Accuracy</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {cameraPerformanceData.map((camera) => (
                  <Table.Tr key={camera.camera}>
                    <Table.Td>
                      <Group gap="sm">
                        <ThemeIcon size="sm" variant="light" color="blue">
                          <IconCamera size={12} />
                        </ThemeIcon>
                        <Text size="sm" fw={500}>{camera.camera}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Progress
                          value={camera.uptime}
                          color={camera.uptime > 98 ? 'green' : camera.uptime > 95 ? 'yellow' : 'red'}
                          style={{ width: 60 }}
                          size="sm"
                        />
                        <Text size="xs" fw={500}>{camera.uptime}%</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{camera.detections}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge color="green" variant="light" size="sm">
                        94.2%
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge
                        color={camera.uptime > 98 ? 'green' : 'orange'}
                        variant="light"
                        size="sm"
                      >
                        {camera.uptime > 98 ? 'Healthy' : 'Warning'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Menu shadow="md" width={180}>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item leftSection={<IconEye style={{ width: rem(14), height: rem(14) }} />}>
                            View Details
                          </Menu.Item>
                          <Menu.Item leftSection={<IconDownload style={{ width: rem(14), height: rem(14) }} />}>
                            Download Report
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="trends" pt="md">
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card>
                <Title order={4} mb="md">Weekly Comparison</Title>
                <LineChart
                  h={250}
                  data={[
                    { week: 'Week 1', thisWeek: 180, lastWeek: 165 },
                    { week: 'Week 2', thisWeek: 195, lastWeek: 180 },
                    { week: 'Week 3', thisWeek: 210, lastWeek: 190 },
                    { week: 'Week 4', thisWeek: 200, lastWeek: 195 },
                  ]}
                  dataKey="week"
                  series={[
                    { name: 'thisWeek', color: 'blue.6' },
                    { name: 'lastWeek', color: 'gray.6' },
                  ]}
                />
              </Card>
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Card>
                <Title order={4} mb="md">Safety Trend Analysis</Title>
                <Stack gap="md">
                  <Alert color="green" title="Positive Trends">
                    <Text size="sm">
                      • 50% reduction in safety incidents
                    </Text>
                    <Text size="sm">
                      • 12% increase in detection accuracy
                    </Text>
                    <Text size="sm">
                      • Faster response times (avg 3.2 min)
                    </Text>
                  </Alert>
                  
                  <Alert color="orange" title="Areas for Improvement">
                    <Text size="sm">
                      • Camera uptime in dining hall
                    </Text>
                    <Text size="sm">
                      • Late afternoon activity monitoring
                    </Text>
                    <Text size="sm">
                      • Weekend coverage gaps
                    </Text>
                  </Alert>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        </Tabs.Panel>
      </Tabs>

      {/* Export Modal */}
      <Modal
        opened={exportModalOpened}
        onClose={closeExportModal}
        title="Export Report"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Export the current report with selected filters and date range.
          </Text>
          
          <Divider />
          
          <SimpleGrid cols={2} spacing="md">
            <Button
              variant="outline"
              leftSection={<IconFileExport size={16} />}
              onClick={() => handleExportReport('pdf')}
              loading={exportReportMutation.isPending}
            >
              Export as PDF
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileExport size={16} />}
              onClick={() => handleExportReport('excel')}
              loading={exportReportMutation.isPending}
            >
              Export as Excel
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileExport size={16} />}
              onClick={() => handleExportReport('csv')}
              loading={exportReportMutation.isPending}
            >
              Export as CSV
            </Button>
            <Button
              variant="outline"
              leftSection={<IconMail size={16} />}
              onClick={() => handleExportReport('email')}
              loading={exportReportMutation.isPending}
            >
              Email Report
            </Button>
          </SimpleGrid>
          
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={closeExportModal}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default Reports;